import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { AdminRecommendationShiftCombobox } from '@apps/web/components/admin/recommendation_explorer/admin_recommendation_shift_combobox';
import { AdminRecommendationShiftGroupCombobox } from '@apps/web/components/admin/recommendation_explorer/admin_recommendation_shift_group_combobox';
import { AxisSelector } from '@apps/web/components/admin/recommendation_explorer/axis_selector';
import {
    activeSelectionOverlayForCategory,
    DEFAULT_RECOMMENDATION_WEIGHTS,
    DEFAULT_SELECTION_OVERLAY_VISIBILITY,
    EMPTY_SELECTED_PLOT_AXES,
    type RecommendationOverlayKey,
    type RecommendationOverlayVisibility,
    type RecommendationWeights,
    recomputeScores,
    type SelectedPlotAxes,
    SELECTION_OVERLAY_LABELS,
    serverScores,
    WEIGHT_KEYS,
    weightsFromResponse,
    weightsToRequest,
} from '@apps/web/components/admin/recommendation_explorer/recommendation_explorer_types';
import { RecommendationResultsTable } from '@apps/web/components/admin/recommendation_explorer/recommendation_results_table';
import {
    buildSelectionSets,
    pickMatchingOutreach,
    selectionCategoryForCaregiver,
    targetShiftIdsForData,
} from '@apps/web/components/admin/recommendation_explorer/selection_overlay';
import { VisualizationPanel } from '@apps/web/components/admin/recommendation_explorer/visualization_panel';
import { WeightControls } from '@apps/web/components/admin/recommendation_explorer/weight_controls';
import { Badge } from '@apps/web/components/ui/badge';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { useAdminOrganization } from '@apps/web/swr_data_fetching/admin_organization_queries';
import { useAdminRecommendationExplorerEvaluation } from '@apps/web/swr_data_fetching/admin_recommendation_explorer';
import {
    useContactAttempts,
    useOutreachesBatch,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { useJoinedOutreach } from '@apps/web/swr_data_fetching/use_joined_outreach';
import { type Mode } from '@libraries/typescript/api_client';
import { type AdminRecommendationExplorerRequest } from '@libraries/typescript/api_client/schema_types';

export const Route = createFileRoute('/_app/admin/recommendation-explorer/')({
    component: RecommendationExplorerPage,
    head: () => ({
        meta: [
            {
                title: 'Recommendation Explorer | Phoebe Admin',
            },
        ],
    }),
});

const LIVE_MODE: Mode = 'live';
type TargetType = 'shift' | 'shift_group';

function shortId(value: string | null | undefined): string {
    return value ? value.slice(0, 8) : 'n/a';
}

function OverlayToggle({
    overlay,
    checked,
    count,
    onCheckedChange,
}: {
    readonly overlay: RecommendationOverlayKey;
    readonly checked: boolean;
    readonly count: number;
    readonly onCheckedChange: (checked: boolean) => void;
}) {
    const inputId = `recommendation-overlay-${overlay}`;
    return (
        <label
            htmlFor={inputId}
            className="border-border flex min-h-9 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs"
        >
            <Checkbox
                id={inputId}
                checked={checked}
                onCheckedChange={(value) => onCheckedChange(value === true)}
            />
            <span className="font-medium">
                {SELECTION_OVERLAY_LABELS[overlay]}
            </span>
            <Badge variant="secondary" className="ml-1 px-1.5">
                {count}
            </Badge>
        </label>
    );
}

function weightsEqual(
    left: RecommendationWeights,
    right: RecommendationWeights,
): boolean {
    return WEIGHT_KEYS.every((key) => left[key] === right[key]);
}

function sortedSetUnion(...sets: Array<ReadonlySet<string>>): Array<string> {
    return Array.from(new Set(sets.flatMap((set) => Array.from(set)))).sort();
}

function RecommendationExplorerPage() {
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [targetType, setTargetType] = useState<TargetType>('shift');
    const [shiftId, setShiftId] = useState<string | null>(null);
    const [shiftGroupId, setShiftGroupId] = useState<string | null>(null);
    // Groups are reachable only through their owning outreach, so we carry the
    // selected group's outreach id into the evaluate request.
    const [shiftGroupOutreachId, setShiftGroupOutreachId] = useState<
        string | null
    >(null);
    const [weights, setWeights] = useState<RecommendationWeights>(
        DEFAULT_RECOMMENDATION_WEIGHTS,
    );
    const [defaultWeights, setDefaultWeights] = useState<RecommendationWeights>(
        DEFAULT_RECOMMENDATION_WEIGHTS,
    );
    const [defaultWeightsTargetKey, setDefaultWeightsTargetKey] = useState<
        string | null
    >(null);
    const [selectedAxes, setSelectedAxes] = useState<SelectedPlotAxes>(
        EMPTY_SELECTED_PLOT_AXES,
    );
    const [hoveredCaregiverId, setHoveredCaregiverId] = useState<string | null>(
        null,
    );
    const [selectedCaregiverId, setSelectedCaregiverId] = useState<
        string | null
    >(null);
    const [overlayVisibility, setOverlayVisibility] =
        useState<RecommendationOverlayVisibility>(
            DEFAULT_SELECTION_OVERLAY_VISIBILITY,
        );

    const { data: organization } = useAdminOrganization(organizationId);
    const targetId = targetType === 'shift' ? shiftId : shiftGroupId;
    const targetKey =
        organizationId && targetId
            ? `${organizationId}:${targetType}:${targetId}`
            : null;
    useEffect(() => {
        setDefaultWeightsTargetKey(null);
        setDefaultWeights(DEFAULT_RECOMMENDATION_WEIGHTS);
        setWeights(DEFAULT_RECOMMENDATION_WEIGHTS);
    }, [targetKey]);
    const baseEvaluationRequest =
        useMemo<AdminRecommendationExplorerRequest | null>(() => {
            if (!organizationId) {
                return null;
            }
            const baseRequest = {
                organization_id: organizationId,
                mode: LIVE_MODE,
                exclude_llm_availability: false,
                always_include_caregiver_ids: [],
                limit: 5000,
            };
            if (targetType === 'shift') {
                if (!shiftId) {
                    return null;
                }
                return {
                    ...baseRequest,
                    shift_id: shiftId,
                };
            }
            if (!shiftGroupId) {
                return null;
            }
            return {
                ...baseRequest,
                shift_group_id: shiftGroupId,
                outreach_id: shiftGroupOutreachId,
                weights: weightsToRequest(weights),
            };
        }, [
            organizationId,
            shiftGroupId,
            shiftGroupOutreachId,
            shiftId,
            targetType,
            weights,
        ]);

    const {
        data: baseData,
        error: baseError,
        isLoading: isLoadingBaseEvaluation,
        isValidating: isValidatingBaseEvaluation,
    } = useAdminRecommendationExplorerEvaluation(baseEvaluationRequest);
    const baseCurrentData =
        baseData?.organization_id === organizationId &&
        baseData.target.target_type === targetType &&
        (targetType === 'shift'
            ? baseData.target.shift_id === shiftId
            : baseData.target.shift_group_id === shiftGroupId)
            ? baseData
            : undefined;
    const targetShiftIds = useMemo(
        () => targetShiftIdsForData(baseCurrentData),
        [baseCurrentData],
    );
    const { data: outreachCandidates, isLoading: isLoadingOutreachCandidates } =
        useOutreachesBatch(
            organizationId,
            targetShiftIds,
            { reason: 'admin-recommendation-explorer-selection-overlay' },
            { outreachTypes: ['callout'] },
            LIVE_MODE,
        );
    const matchingOutreachCandidate = useMemo(
        () => pickMatchingOutreach(outreachCandidates ?? [], targetShiftIds),
        [outreachCandidates, targetShiftIds],
    );
    const { data: matchingOutreach, isLoading: isLoadingMatchingOutreach } =
        useJoinedOutreach(
            organizationId,
            matchingOutreachCandidate?.id ?? null,
            {
                reason: 'admin-recommendation-explorer-selection-overlay',
            },
            LIVE_MODE,
        );
    const matchingOutreachId =
        matchingOutreach?.id ?? matchingOutreachCandidate?.id ?? null;
    const matchedOutreachForDisplay =
        matchingOutreach ?? matchingOutreachCandidate;
    const hasMatchingOutreachCandidate = matchingOutreachCandidate !== null;
    const { data: contactAttempts, isLoading: isLoadingContactAttempts } =
        useContactAttempts(
            organizationId,
            matchingOutreachId,
            {
                reason: 'admin-recommendation-explorer-selection-overlay',
            },
            LIVE_MODE,
        );
    const hasTarget =
        targetType === 'shift' ? shiftId !== null : shiftGroupId !== null;
    const hasSelectionData =
        hasTarget &&
        !isLoadingOutreachCandidates &&
        (matchingOutreachCandidate === null ||
            matchingOutreach !== undefined) &&
        (matchingOutreachId === null || contactAttempts !== undefined);
    const selectionSets = useMemo(
        () =>
            hasSelectionData
                ? buildSelectionSets(
                      matchingOutreach ?? null,
                      contactAttempts ?? [],
                      targetShiftIds,
                  )
                : buildSelectionSets(null, [], targetShiftIds),
        [contactAttempts, hasSelectionData, matchingOutreach, targetShiftIds],
    );
    const selectedOrSuggestedCaregiverIds = useMemo(
        () =>
            sortedSetUnion(
                selectionSets.agentSuggestedIds,
                selectionSets.schedulerSelectedIds,
            ),
        [selectionSets],
    );
    const attributedEvaluationRequest = useMemo(() => {
        if (
            !hasSelectionData ||
            !baseEvaluationRequest ||
            selectedOrSuggestedCaregiverIds.length === 0
        ) {
            return null;
        }
        return {
            ...baseEvaluationRequest,
            always_include_caregiver_ids: selectedOrSuggestedCaregiverIds,
        };
    }, [
        baseEvaluationRequest,
        hasSelectionData,
        selectedOrSuggestedCaregiverIds,
    ]);
    const {
        data: attributedData,
        error: attributedError,
        isLoading: isLoadingAttributedEvaluation,
        isValidating: isValidatingAttributedEvaluation,
    } = useAdminRecommendationExplorerEvaluation(attributedEvaluationRequest);
    const isAttributedEvaluationPending =
        attributedEvaluationRequest !== null &&
        (isLoadingAttributedEvaluation || isValidatingAttributedEvaluation);
    const attributedCurrentData =
        attributedEvaluationRequest !== null &&
        !isAttributedEvaluationPending &&
        attributedData?.organization_id === organizationId &&
        attributedData.target.target_type === targetType &&
        (targetType === 'shift'
            ? attributedData.target.shift_id === shiftId
            : attributedData.target.shift_group_id === shiftGroupId)
            ? attributedData
            : undefined;
    const isAttributionUnavailable =
        attributedEvaluationRequest !== null &&
        !isAttributedEvaluationPending &&
        attributedError !== undefined;
    const currentData =
        attributedEvaluationRequest === null || isAttributionUnavailable
            ? baseCurrentData
            : attributedCurrentData;
    const displaySelectionSets = useMemo(
        () =>
            isAttributionUnavailable
                ? buildSelectionSets(null, [], targetShiftIds)
                : selectionSets,
        [isAttributionUnavailable, selectionSets, targetShiftIds],
    );
    const responseWeights =
        currentData === undefined
            ? undefined
            : weightsFromResponse(currentData.weights);
    const error =
        baseError ??
        (attributedEvaluationRequest === null ? undefined : attributedError);
    const isLoading =
        isLoadingBaseEvaluation ||
        (attributedEvaluationRequest !== null &&
            !isAttributionUnavailable &&
            isAttributedEvaluationPending);
    const isValidating =
        isValidatingBaseEvaluation || isValidatingAttributedEvaluation;
    const isWaitingForServerWeights =
        targetType === 'shift_group' &&
        currentData !== undefined &&
        responseWeights !== undefined &&
        !weightsEqual(responseWeights, weights);
    const isUpdatingGroupScores =
        targetType === 'shift_group' &&
        currentData !== undefined &&
        (isWaitingForServerWeights || isValidating);
    useEffect(() => {
        if (!currentData || !targetKey || defaultWeightsTargetKey !== null) {
            return;
        }
        const nextWeights = weightsFromResponse(currentData.weights);
        setDefaultWeights(nextWeights);
        setWeights(nextWeights);
        setDefaultWeightsTargetKey(targetKey);
    }, [currentData, defaultWeightsTargetKey, targetKey]);
    const recomputedCaregivers = useMemo(
        () =>
            targetType === 'shift'
                ? recomputeScores(currentData?.caregivers ?? [], weights)
                : serverScores(currentData?.caregivers ?? []),
        [currentData?.caregivers, targetType, weights],
    );
    const taggedCaregivers = useMemo(
        () =>
            recomputedCaregivers.map((caregiver) => {
                const selectionCategory = selectionCategoryForCaregiver(
                    caregiver.caregiver_id,
                    displaySelectionSets,
                );
                return {
                    ...caregiver,
                    selection_category: selectionCategory,
                    active_selection_overlay: activeSelectionOverlayForCategory(
                        selectionCategory,
                        overlayVisibility,
                    ),
                };
            }),
        [displaySelectionSets, overlayVisibility, recomputedCaregivers],
    );
    const activeCaregiverId = hoveredCaregiverId ?? selectedCaregiverId;
    const isLoadingSelectionData =
        isLoadingOutreachCandidates ||
        (hasMatchingOutreachCandidate && isLoadingMatchingOutreach) ||
        (matchingOutreachId !== null && isLoadingContactAttempts);

    function setOverlayVisibilityForKey(
        overlay: RecommendationOverlayKey,
        checked: boolean,
    ) {
        setOverlayVisibility((current) => ({
            ...current,
            [overlay]: checked,
        }));
    }

    return (
        <div className="flex flex-col gap-4 p-4 md:p-6">
            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-72">
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        Organization
                    </div>
                    <OrganizationCombobox
                        value={organizationId ? [organizationId] : []}
                        onValueChange={(ids) => {
                            const nextOrganizationId =
                                ids.find((id) => id !== organizationId) ?? null;
                            setOrganizationId(nextOrganizationId);
                            setShiftId(null);
                            setShiftGroupId(null);
                            setHoveredCaregiverId(null);
                            setSelectedCaregiverId(null);
                        }}
                        placeholder="Select organization"
                    />
                </div>
                <div>
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        Target
                    </div>
                    <Tabs
                        value={targetType}
                        onValueChange={(value) => {
                            const nextTargetType = value as TargetType;
                            setTargetType(nextTargetType);
                            setShiftId(null);
                            setShiftGroupId(null);
                            setHoveredCaregiverId(null);
                            setSelectedCaregiverId(null);
                        }}
                    >
                        <TabsList>
                            <TabsTrigger value="shift">Shift</TabsTrigger>
                            <TabsTrigger value="shift_group">Group</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <div className="min-w-96">
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        {targetType === 'shift' ? 'Shift' : 'Shift Group'}
                    </div>
                    {targetType === 'shift' ? (
                        <AdminRecommendationShiftCombobox
                            organizationId={organizationId}
                            value={shiftId}
                            onValueChange={(nextShiftId) => {
                                setShiftId(nextShiftId);
                                setHoveredCaregiverId(null);
                                setSelectedCaregiverId(null);
                            }}
                            timezone={organization?.timezone}
                            mode={LIVE_MODE}
                        />
                    ) : (
                        <AdminRecommendationShiftGroupCombobox
                            organizationId={organizationId}
                            value={shiftGroupId}
                            onValueChange={(shiftGroup) => {
                                setShiftGroupId(
                                    shiftGroup?.shift_group_id ?? null,
                                );
                                setShiftGroupOutreachId(
                                    shiftGroup?.outreach_id ?? null,
                                );
                                setHoveredCaregiverId(null);
                                setSelectedCaregiverId(null);
                            }}
                            mode={LIVE_MODE}
                            timezone={organization?.timezone}
                        />
                    )}
                </div>
                <div className="min-w-[20rem]">
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        Plot Axes
                    </div>
                    <AxisSelector
                        selectedAxes={selectedAxes}
                        onSelectedAxesChange={setSelectedAxes}
                    />
                </div>
                {currentData && (
                    <Badge variant="secondary" className="mb-1 h-9 px-3">
                        {currentData.returned_count} returned /{' '}
                        {currentData.total_scored_count} scored
                    </Badge>
                )}
                {isUpdatingGroupScores && (
                    <Badge variant="outline" className="mb-1 h-9 px-3">
                        Updating
                    </Badge>
                )}
            </div>
            <WeightControls
                weights={weights}
                defaultWeights={defaultWeights}
                onWeightsChange={setWeights}
            />
            {currentData && (
                <section className="border-border bg-background rounded-md border px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-sm font-semibold">
                                Scheduler Overlays
                            </h2>
                            <p className="text-muted-foreground text-xs">
                                {matchedOutreachForDisplay
                                    ? `Outreach ${shortId(matchedOutreachForDisplay.id)}`
                                    : isLoadingSelectionData
                                      ? 'Loading outreach data'
                                      : 'No matching outreach for selected shifts'}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <OverlayToggle
                                overlay="agent_recommended"
                                checked={overlayVisibility.agent_recommended}
                                count={
                                    displaySelectionSets.agentSuggestedIds.size
                                }
                                onCheckedChange={(checked) =>
                                    setOverlayVisibilityForKey(
                                        'agent_recommended',
                                        checked,
                                    )
                                }
                            />
                            <OverlayToggle
                                overlay="scheduler_selected"
                                checked={overlayVisibility.scheduler_selected}
                                count={
                                    displaySelectionSets.schedulerSelectedIds
                                        .size
                                }
                                onCheckedChange={(checked) =>
                                    setOverlayVisibilityForKey(
                                        'scheduler_selected',
                                        checked,
                                    )
                                }
                            />
                            <OverlayToggle
                                overlay="suggested_and_selected"
                                checked={
                                    overlayVisibility.suggested_and_selected
                                }
                                count={
                                    displaySelectionSets.suggestedAndSelectedIds
                                        .size
                                }
                                onCheckedChange={(checked) =>
                                    setOverlayVisibilityForKey(
                                        'suggested_and_selected',
                                        checked,
                                    )
                                }
                            />
                            <OverlayToggle
                                overlay="selected_without_agent_suggestion"
                                checked={
                                    overlayVisibility.selected_without_agent_suggestion
                                }
                                count={
                                    displaySelectionSets
                                        .selectedWithoutSuggestionIds.size
                                }
                                onCheckedChange={(checked) =>
                                    setOverlayVisibilityForKey(
                                        'selected_without_agent_suggestion',
                                        checked,
                                    )
                                }
                            />
                        </div>
                    </div>
                </section>
            )}
            {!organizationId && (
                <div className="text-muted-foreground rounded-md border p-8 text-center text-sm">
                    Select an organization to start.
                </div>
            )}
            {organizationId && targetType === 'shift' && !shiftId && (
                <div className="text-muted-foreground rounded-md border p-8 text-center text-sm">
                    Select a shift to evaluate.
                </div>
            )}
            {organizationId &&
                targetType === 'shift_group' &&
                !shiftGroupId && (
                    <div className="text-muted-foreground rounded-md border p-8 text-center text-sm">
                        Select a shift group to evaluate.
                    </div>
                )}
            {baseEvaluationRequest &&
                hasTarget &&
                (isLoading || !currentData) && (
                    <div className="text-muted-foreground flex items-center gap-2 rounded-md border p-8 text-sm">
                        <Spinner />
                        Loading recommendations
                    </div>
                )}
            {error && (
                <div className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border p-4 text-sm">
                    {error.message}
                </div>
            )}
            {currentData && (
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(36rem,1.3fr)]">
                    <VisualizationPanel
                        caregivers={taggedCaregivers}
                        selectedAxes={selectedAxes}
                        activeCaregiverId={activeCaregiverId}
                        onActiveCaregiverIdChange={setHoveredCaregiverId}
                        onSelectedCaregiverIdChange={setSelectedCaregiverId}
                    />
                    <RecommendationResultsTable
                        caregivers={taggedCaregivers}
                        activeCaregiverId={activeCaregiverId}
                        selectedCaregiverId={selectedCaregiverId}
                        onHoveredCaregiverIdChange={setHoveredCaregiverId}
                        onSelectedCaregiverIdChange={setSelectedCaregiverId}
                        truncated={currentData.truncated}
                        totalScoredCount={currentData.total_scored_count}
                        returnedCount={currentData.returned_count}
                        scoreMode={targetType === 'shift' ? 'client' : 'server'}
                        columnVisibilityKey={`admin-recommendation-explorer-${targetType}-columns-v3`}
                    />
                </div>
            )}
        </div>
    );
}
